import { supabaseClient, useUser } from '@auth-v2';
import { useMutation, useQuery } from '@tanstack/react-query';

import {
    createLayoutCustomizations,
    fetchCustomModuleCustomizations,
    LayoutCustomization,
    omitCustomModuleCustomizations,
} from '@data';
import { activeUserIdAtom, useEnsureAtomValue } from '@store';

import { queryClient } from '../config/client';
import { queryKeys } from '../queryKeys';

export const getCustomModuleCustomizationsKey = (layoutId: number, moduleId: string) =>
    ['layout', layoutId, 'customizations', moduleId] as const;

export const getCustomTableMutationKey = (layoutId: number, moduleId: string) =>
    ['layout', layoutId, 'customizations', moduleId, 'tableModule'] as const;

export const useCustomModule = (layoutId: number, moduleId: string) => {
    const { data: user } = useUser();
    return useQuery({
        queryKey: getCustomModuleCustomizationsKey(layoutId, moduleId),
        queryFn: () => fetchCustomModuleCustomizations(supabaseClient, layoutId, moduleId),
        enabled: !!user && layoutId > 0,
    });
};

export const useSaveCustomModule = (layoutId: number, moduleId: string) => {
    const activeUserId = useEnsureAtomValue(activeUserIdAtom);
    return useMutation({
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: getCustomModuleCustomizationsKey(layoutId, moduleId),
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.layoutCustomization.key(activeUserId, layoutId),
            });
        },

        mutationFn: async (selectedParameterIds: string[]) => {
            const currentCustomizations = queryClient.getQueryData<LayoutCustomization[]>(
                getCustomModuleCustomizationsKey(layoutId, moduleId),
            );

            if (!currentCustomizations) {
                throw new Error(`No customizations loaded: layoutId ${layoutId}, moduleId ${moduleId}`);
            }
            const currentParameterIds = currentCustomizations
                .filter((customization) => customization.parentUiNodeId === moduleId)
                .map((customization) => customization.childUiNodeId);

            const parametersIdToOmit = currentParameterIds.filter(
                (parameterId) => !selectedParameterIds.includes(parameterId),
            );
            await omitCustomModuleCustomizations(supabaseClient, layoutId, moduleId, parametersIdToOmit);

            const rootNode = currentCustomizations.find(
                (customization) => !customization.parentUiNodeId && customization.childUiNodeId === moduleId,
            );

            const paramtersIdToAdd = selectedParameterIds.filter(
                (parameterId) => !currentParameterIds.includes(parameterId),
            );

            const newLayoutCustomizations: Omit<LayoutCustomization, 'id'>[] = paramtersIdToAdd.map((parameterId) => ({
                parentUiNodeId: moduleId,
                childUiNodeId: parameterId,
                showChildOn: ['kiosk', 'floor'],
                childDisplayOrder: null,
                childMetadata: {},
            }));

            await createLayoutCustomizations(
                supabaseClient,
                layoutId,
                rootNode
                    ? newLayoutCustomizations
                    : [
                          {
                              parentUiNodeId: null,
                              childUiNodeId: moduleId,
                              showChildOn: ['kiosk'],
                              childDisplayOrder: 1,
                              childMetadata: {
                                  title: 'My Custom Module',
                              },
                          },
                          ...newLayoutCustomizations,
                      ],
            );
        },
    });
};
